const checkCompatibility = async () => {
  // Check for required APIs
  const requirements = {
    mediaDevices: !!navigator.mediaDevices,
    getUserMedia: !!navigator.mediaDevices?.getUserMedia,
    mediaRecorder: !!window.MediaRecorder,
    fileReader: !!window.FileReader,
    webWorkers: !!window.Worker,
  };

  // Check for sufficient storage
  let storage = null;
  try {
    // Method 1: Modern Storage API
    if (navigator.storage && typeof navigator.storage.estimate === "function") {
      storage = await navigator.storage.estimate();
    }
    // Method 2: WebkitTemporaryStorage API (older Safari)
    else if (
      navigator.webkitTemporaryStorage &&
      typeof navigator.webkitTemporaryStorage.queryUsageAndQuota === "function"
    ) {
      storage = await new Promise((resolve) => {
        navigator.webkitTemporaryStorage.queryUsageAndQuota(
          (used, granted) => {
            resolve({ usage: used, quota: granted });
          },
          (error) => {
            console.warn("Storage estimation failed:", error);
            resolve({ quota: 1024 * 1024 * 1024 }); // 1GB fallback
          }
        );
      });
    }
    // Method 3: Fallback for unsupported browsers
    else {
      console.warn("Storage API not supported, using fallback value");
      storage = {
        quota: 1024 * 1024 * 1024, // 1GB fallback
        usage: 0,
      };
    }
  } catch (error) {
    console.warn("Error checking storage:", error);
    storage = {
      quota: 1024 * 1024 * 1024, // 1GB fallback
      usage: 0,
    };
  }

  //Check for browser compatibility
  // Check browser
  const browser = {
    name: getBrowserName(),
    version: getBrowserVersion(),
    isSupported: checkBrowserSupport(),
  };

  return {
    ...requirements,
    storage: storage?.quota || 0,
    storageUsage: storage?.usage || 0,
    browser,
  };
};

// Helper functions for browser detection
const getBrowserName = () => {
  const userAgent = navigator.userAgent.toLowerCase();

  if (userAgent.includes("edg/")) return "Edge";
  if (userAgent.includes("chrome")) return "Chrome";
  if (userAgent.includes("firefox")) return "Firefox";
  if (userAgent.includes("safari") && !userAgent.includes("chrome"))
    return "Safari";
  return "Unknown";
};

const getBrowserVersion = () => {
  const userAgent = navigator.userAgent;
  let match;

  // Edge
  if ((match = userAgent.match(/(edg(?=\/))\/?\s*(\d+)/i))) {
    return match[2];
  }
  // Chrome, Firefox, Safari
  if ((match = userAgent.match(/(chrome|firefox|safari)\/(\d+)/i))) {
    return match[2];
  }
  return "Unknown";
};

const checkBrowserSupport = () => {
  const browser = getBrowserName();
  const version = parseInt(getBrowserVersion());

  // Define minimum supported versions
  const minVersions = {
    Chrome: 60,
    Firefox: 60,
    Safari: 14,
    Edge: 79,
  };

  return version >= (minVersions[browser] || Infinity);
};

export const checkDeviceReadiness = async () => {
  console.log("Checking device readiness");
  const compatibility = await checkCompatibility();

  if (!compatibility.mediaDevices) {
    throw new Error("Camera access not supported");
  }

  if (!compatibility.mediaRecorder) {
    throw new Error("Video recording not supported");
  }

  if (!compatibility.fileReader) {
    throw new Error("File reading not supported");
  }

  if (!compatibility.webWorkers) {
    throw new Error("Web Workers not supported");
  }

  // Storage check - require at least 100MB
  const minStorage = 1024 * 1024 * 100; // 100MB
  if (!compatibility.storage || compatibility.storage < minStorage) {
    throw new Error(
      "Insufficient browser storage space (minimum 100MB required)"
    );
  }
  if (!compatibility.browser.isSupported) {
    throw new Error(
      "Browser not supported. Please use a recent version of Chrome, Firefox, Safari, or Edge."
    );
  }

  return {
    ...compatibility,
    supported: true,
  };
};
